import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
 Badge,
 Button,
 Card,
 Col,
 Form,
 Modal,
 Row,
 Spinner,
 Table,
} from "react-bootstrap";
import swal from "sweetalert";
import api from "../../../api";
import PageTitle from "../../layouts/PageTitle";

const COMMON_FIELDS = [
 { key: "srNo", label: "SR.NO" },
 { key: "location", label: "LOCATION" },
 { key: "areaNo", label: "AREA NO." },
 { key: "uom", label: "UOM" },
 { key: "from", label: "FROM" },
 { key: "to", label: "TO" },
 { key: "wireSize", label: "WIRE SIZES" },
 { key: "circuitNo", label: "CIRCUIT NO." },
 { key: "conduitSize", label: "CONDUIT SIZE" },
 { key: "remarks", label: "REMARKS" },
 { key: "action", label: "ACTION" },
];

const createEmptyCustomColumns = () =>
 Array.from({ length: 20 }, (_, index) => ({
 key: `col${index + 1}`,
 enabled: false,
 label: "",
 groupLabel: "",
 groupSpan: 1,
 fieldType: "number",
 }));

const emptyForm = () => ({
 code: "",
 name: "",
 description: "",
 status: "Active",
 commonFields: COMMON_FIELDS.map((field) => ({
 ...field,
 enabled: ["srNo", "location", "uom", "remarks", "action"].includes(field.key),
 })),
 customColumns: createEmptyCustomColumns(),
});

const MeasurementSheetTypeMaster = () => {
 const [list, setList] = useState([]);
 const [filtered, setFiltered] = useState([]);
 const [loading, setLoading] = useState(false);
 const [saving, setSaving] = useState(false);
 const [showModal, setShowModal] = useState(false);
 const [editId, setEditId] = useState(null);
 const [search, setSearch] = useState("");
 const [form, setForm] = useState(emptyForm());

 const fetchTypes = async () => {
 setLoading(true);
 try {
 const res = await api.get("/measurement-sheet-types?includeInactive=true");
 if (res.data.success) {
 setList(res.data.data || []);
 setFiltered(res.data.data || []);
 }
 } catch (error) {
 swal(
 "Error",
 error.response?.data?.error ||
 error.response?.data?.message ||
 error.message ||
 "Failed to load measurement sheet types",
 "error",
 );
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
 fetchTypes();
 }, []);

 useEffect(() => {
 if (!search.trim()) {
 setFiltered(list);
 return;
 }

 const value = search.toLowerCase();
 setFiltered(
 list.filter(
 (item) =>
 item.name?.toLowerCase().includes(value) ||
 item.code?.toLowerCase().includes(value),
 ),
 );
 }, [search, list]);

 const openAdd = () => {
 setEditId(null);
 setForm(emptyForm());
 setShowModal(true);
 };

 const openEdit = (item) => {
 const commonFields = COMMON_FIELDS.map((field) => ({
 ...field,
 enabled: item.defaultColumnConfig?.[field.key] !== false && !!item.defaultColumnConfig?.[field.key],
 }));

 const customColumns = createEmptyCustomColumns().map((column) => {
 const colSpan = item.defaultColumnSettings?.[column.key]?.colSpan || 1;
 const fieldType = item.defaultColumnSettings?.[column.key]?.fieldType || "number";
 return {
 ...column,
 enabled: !!item.defaultColumnConfig?.[column.key],
 label: item.defaultHeaders?.[column.key] || "",
 groupLabel: item.defaultHeaders?.[`${column.key}_group`] || "",
 groupSpan: colSpan,
 fieldType: fieldType,
 };
 });

 setEditId(item._id);
 setForm({
 code: item.code || "",
 name: item.name || "",
 description: item.description || "",
 status: item.status || "Active",
 commonFields,
 customColumns,
 });
 setShowModal(true);
 };

 const closeModal = () => {
 if (!saving) setShowModal(false);
 };

 const enabledColumns = useMemo(
 () => form.customColumns.filter((column) => column.enabled),
 [form.customColumns],
 );

 const enabledCommonFields = useMemo(
 () => form.commonFields.filter((field) => field.enabled),
 [form.commonFields],
 );

 const buildPayload = () => {
 const defaultHeaders = {};
 const defaultColumnConfig = {};
 const defaultColumnSettings = {};
 const fields = [];

 form.commonFields.forEach((field, index) => {
 if (!field.enabled) return;
 defaultHeaders[field.key] = field.label;
 defaultColumnConfig[field.key] = true;
 fields.push({
 key: field.key,
 label: field.label,
 order: index + 1,
 inputType: ["location", "remarks", "from", "to", "wireSize", "circuitNo", "conduitSize"].includes(field.key)
 ? "text"
 : field.key === "uom"
 ? "select"
 : "number",
 });
 });

 form.customColumns.forEach((column, index) => {
 if (!column.enabled) return;
 defaultHeaders[column.key] = column.label || column.key.toUpperCase();
 defaultColumnConfig[column.key] = true;

 const span = Number(column.groupSpan || 1);
 const fieldType = column.fieldType || "number";
 
 defaultColumnSettings[column.key] = { 
 ...defaultColumnSettings[column.key],
 fieldType: fieldType 
 };

 if (column.groupLabel && span > 1) {
 defaultHeaders[`${column.key}_group`] = column.groupLabel;
 defaultColumnSettings[column.key].colSpan = span;
 }

 fields.push({
 key: column.key,
 label: column.label || column.key.toUpperCase(),
 groupKey: column.groupLabel ? `${column.key}_group` : "",
 groupLabel: column.groupLabel || "",
 order: 100 + index,
 inputType: fieldType,
 });
 });

 return {
 code: form.code,
 name: form.name,
 description: form.description,
 status: form.status,
 fields,
 defaultHeaders,
 defaultColumnConfig,
 defaultColumnSettings,
 };
 };

 const handleSave = async () => {
 if (!form.code.trim() || !form.name.trim()) {
 return swal("Validation", "Code and name are required", "warning");
 }

 if (!enabledColumns.length && !enabledCommonFields.length) {
 return swal("Validation", "Enable at least one common field or custom column", "warning");
 }

 try {
 setSaving(true);
 const payload = buildPayload();

 if (editId) {
 await api.put(`/measurement-sheet-types/${editId}`, payload);
 } else {
 await api.post("/measurement-sheet-types", payload);
 }

 swal("Success", "Measurement sheet type saved successfully", "success");
 setShowModal(false);
 fetchTypes();
 } catch (error) {
 swal(
 "Error",
 error.response?.data?.message || "Failed to save measurement sheet type",
 "error",
 );
 } finally {
 setSaving(false);
 }
 };

 const deactivate = (item) => {
 swal({
 title: `Deactivate "${item.name}"?`,
 text: "This custom type will no longer be selectable.",
 icon: "warning",
 buttons: true,
 dangerMode: true,
 }).then(async (ok) => {
 if (!ok) return;
 try {
 await api.delete(`/measurement-sheet-types/${item._id}`);
 fetchTypes();
 } catch (error) {
 swal(
 "Error",
 error.response?.data?.message || "Cannot deactivate this type",
 "error",
 );
 }
 });
 };

 return (
 <Fragment>
 <PageTitle activeMenu="Measurement Sheet Type Master" motherMenu="Masters" />

 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center">
 <Card.Title>Measurement Sheet Types</Card.Title>
 <Button onClick={openAdd}>+ Add Sheet Type</Button>
 </Card.Header>
 <Card.Body>
 <Form.Control
 className="mb-3"
 placeholder="Search by code or name..."
 value={search}
 onChange={(e) => setSearch(e.target.value)}
 />

 {loading ? (
 <div className="text-center py-5">
 <Spinner animation="border" />
 </div>
 ) : (
 <Table bordered hover responsive>
 <thead className="table-light">
 <tr>
 <th>Code</th>
 <th>Name</th>
 <th>Type</th>
 <th>Status</th>
 <th>Columns</th>
 <th width="180">Actions</th>
 </tr>
 </thead>
 <tbody>
 {filtered.map((item) => (
 <tr key={item._id}>
 <td>{item.code}</td>
 <td>{item.name}</td>
 <td>
 <Badge bg={item.isLocked ? "primary" : "info"}>
 {item.isLocked ? "Default" : "Custom"}
 </Badge>
 </td>
 <td>
 <Badge bg={item.status === "Active" ? "success" : "secondary"}>
 {item.status}
 </Badge>
 </td>
 <td>{Object.keys(item.defaultColumnConfig || {}).filter((key) => item.defaultColumnConfig[key]).length}</td>
 <td>
 {!item.isLocked && (
 <Button
 size="sm"
 variant="warning"
 className="me-2"
 onClick={() => openEdit(item)}
 >
 Edit
 </Button>
 )}

 {!item.isLocked && item.status === "Active" && (
 <Button
 size="sm"
 variant="danger"
 onClick={() => deactivate(item)}
 >
 Deactivate
 </Button>
 )}

 {item.isLocked && <span className="text-muted small">Locked default</span>}
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>

 <Modal
 show={showModal}
 onHide={closeModal}
 size="xl"
 backdrop={saving ? "static" : true}
 >
 <Modal.Header closeButton={!saving}>
 <Modal.Title>{editId ? "Edit Sheet Type" : "Add Sheet Type"}</Modal.Title>
 </Modal.Header>
 <Modal.Body>
 <Row className="g-3">
 <Col md={4}>
 <Form.Group>
 <Form.Label>Code *</Form.Label>
 <Form.Control
 value={form.code}
 onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "_") }))}
 disabled={saving || !!editId}
 placeholder="MANUAL_CUSTOM_SHEET"
 />
 </Form.Group>
 </Col>
 <Col md={4}>
 <Form.Group>
 <Form.Label>Name *</Form.Label>
 <Form.Control
 value={form.name}
 onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
 disabled={saving}
 placeholder="Manual Custom Sheet"
 />
 </Form.Group>
 </Col>
 <Col md={4}>
 <Form.Group>
 <Form.Label>Status</Form.Label>
 <Form.Select
 value={form.status}
 onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
 disabled={saving}
 >
 <option value="Active">Active</option>
 <option value="Inactive">Inactive</option>
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={12}>
 <Form.Group>
 <Form.Label>Description</Form.Label>
 <Form.Control
 as="textarea"
 rows={2}
 value={form.description}
 onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
 disabled={saving}
 />
 </Form.Group>
 </Col>
 </Row>

 <hr />

 <h5 className="mb-3">Common Fields</h5>
 <Row className="g-2">
 {form.commonFields.map((field, index) => (
 <Col md={4} key={field.key}>
 <div className="border rounded p-2">
 <Form.Check
 type="switch"
 label={field.key}
 checked={field.enabled}
 onChange={(e) =>
 setForm((prev) => {
 const commonFields = [...prev.commonFields];
 commonFields[index] = {
 ...commonFields[index],
 enabled: e.target.checked,
 };
 return { ...prev, commonFields };
 })
 }
 />
 <Form.Control
 className="mt-2"
 value={field.label}
 onChange={(e) =>
 setForm((prev) => {
 const commonFields = [...prev.commonFields];
 commonFields[index] = {
 ...commonFields[index],
 label: e.target.value,
 };
 return { ...prev, commonFields };
 })
 }
 disabled={saving}
 />
 </div>
 </Col>
 ))}
 </Row>

 <hr />

 <h5 className="mb-3">Custom Columns</h5>
 <Table bordered responsive>
 <thead className="table-light">
 <tr>
 <th style={{ width: 90 }}>Use</th>
 <th style={{ width: 120 }}>Key</th>
 <th>Column Label</th>
 <th style={{ width: 150 }}>Data Type</th>
 <th>Group Label</th>
 <th style={{ width: 120 }}>Group Span</th>
 </tr>
 </thead>
 <tbody>
 {form.customColumns.map((column, index) => (
 <tr key={column.key}>
 <td>
 <Form.Check
 type="checkbox"
 checked={column.enabled}
 onChange={(e) =>
 setForm((prev) => {
 const customColumns = [...prev.customColumns];
 customColumns[index] = {
 ...customColumns[index],
 enabled: e.target.checked,
 };
 return { ...prev, customColumns };
 })
 }
 />
 </td>
 <td>{column.key}</td>
 <td>
 <Form.Control
 value={column.label}
 onChange={(e) =>
 setForm((prev) => {
 const customColumns = [...prev.customColumns];
 customColumns[index] = {
 ...customColumns[index],
 label: e.target.value,
 };
 return { ...prev, customColumns };
 })
 }
 disabled={!column.enabled || saving}
 placeholder={`Label for ${column.key}`}
 />
 </td>
 <td>
 <Form.Select
 value={column.fieldType}
 onChange={(e) =>
 setForm((prev) => {
 const customColumns = [...prev.customColumns];
 customColumns[index] = {
 ...customColumns[index],
 fieldType: e.target.value,
 };
 return { ...prev, customColumns };
 })
 }
 disabled={!column.enabled || saving}
 >
 <option value="number">Number</option>
 <option value="text">Text</option>
 </Form.Select>
 </td>
 <td>
 <Form.Control
 value={column.groupLabel}
 onChange={(e) =>
 setForm((prev) => {
 const customColumns = [...prev.customColumns];
 customColumns[index] = {
 ...customColumns[index],
 groupLabel: e.target.value,
 };
 return { ...prev, customColumns };
 })
 }
 disabled={!column.enabled || saving}
 placeholder="Optional group heading"
 />
 </td>
 <td>
 <Form.Control
 type="number"
 min={1}
 max={20 - index}
 value={column.groupSpan}
 onChange={(e) =>
 setForm((prev) => {
 const customColumns = [...prev.customColumns];
 customColumns[index] = {
 ...customColumns[index],
 groupSpan: Number(e.target.value || 1),
 };
 return { ...prev, customColumns };
 })
 }
 disabled={!column.enabled || saving}
 />
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 <div className="small text-muted">
 Use `Group Label` and `Group Span` only on the first column of a grouped header.
 </div>
 </Modal.Body>
 <Modal.Footer>
 <Button variant="secondary" onClick={closeModal} disabled={saving}>
 Cancel
 </Button>
 <Button onClick={handleSave} disabled={saving}>
 {saving ? "Saving..." : "Save"}
 </Button>
 </Modal.Footer>
 </Modal>
 </Fragment>
 );
};

export default MeasurementSheetTypeMaster;
